import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AuthProvider, RequireAuth, RequireAdmin, useAuth } from "@/context/AuthContext";
import { LangContext, makeTr } from "@/lib/i18n";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import Formations from "@/pages/Formations";
import Philosophie from "@/pages/Philosophie";
import FormationDetail from "@/pages/FormationDetail";
import Galerie from "@/pages/Galerie";
import EcolePublic from "@/pages/EcolePublic";
import Ambassadeurs from "@/pages/Ambassadeurs";
import Cgv from "@/pages/Cgv";
import MentionsLegales from "@/pages/MentionsLegales";
import Temoignages from "@/pages/Temoignages";
import Presse from "@/pages/Presse";
import Certification from "@/pages/Certification";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Ecole from "@/pages/Ecole";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import "@/App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-abyss grid place-items-center p-8 text-center">
          <div>
            <p className="font-serif text-3xl text-foam">La page a perdu le Nord.</p>
            <button
              className="mt-6 rounded-full bg-aqua text-abyss px-6 py-3 text-sm font-semibold"
              onClick={() => window.location.reload()}
              data-testid="error-reload-btn"
            >
              Recharger
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell() {
  const { user } = useAuth();
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/formations" element={<Formations />} />
        <Route path="/philosophie" element={<Philosophie />} />
        <Route path="/galerie" element={<Galerie />} />
        <Route path="/ecole" element={<EcolePublic />} />
        <Route path="/ambassadeurs" element={<Ambassadeurs />} />
        <Route path="/cgv" element={<Cgv />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/temoignages" element={<Temoignages />} />
        <Route path="/presse" element={<Presse />} />
        <Route path="/certification" element={<Certification />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/formations/:slug" element={<FormationDetail />} />
        <Route path="/connexion" element={<Auth />} />
        <Route
          path="/espace-ecole"
          element={
            <RequireAdmin>
              <Ecole />
            </RequireAdmin>
          }
        />
        <Route
          path="/tableau-de-bord"
          element={
            <RequireAuth>
              <Dashboard />
            </RequireAuth>
          }
        />
        <Route path="/paiement/succes" element={<PaymentSuccess />} />
        <Route path="/paiement/annule" element={<PaymentCancel />} />
        <Route path="*" element={<Landing />} />
      </Routes>
      {user !== null && <Footer />}
    </>
  );
}

function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("ojanzu-lang") || "fr");
  useEffect(() => {
    localStorage.setItem("ojanzu-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const tr = makeTr(lang);
  return <LangContext.Provider value={{ lang, setLang, tr }}>{children}</LangContext.Provider>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <SmoothScroll />
            <ScrollToTop />
            <div className="noise" />
            <Shell />
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
